import { useCallback, useEffect, useRef } from 'react';
import { REELS_LIBRARY_REVALIDATE_DEBOUNCE_MS } from '../config';

/**
 * Refetch the reels library when the tab/window returns.
 * Cross-device deletes (and renames) otherwise stick as a stale mount snapshot
 * because all-delivered libraries do not poll.
 */
export function useReelsLibraryRevalidate(
  load: () => void | Promise<void>,
  enabled: boolean
): void {
  const loadRef = useRef(load);
  loadRef.current = load;
  const inFlightRef = useRef(false);
  const lastAtRef = useRef(0);

  const run = useCallback(() => {
    if (!enabled) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return;
    }
    const now = Date.now();
    if (now - lastAtRef.current < REELS_LIBRARY_REVALIDATE_DEBOUNCE_MS) return;
    if (inFlightRef.current) return;
    lastAtRef.current = now;
    inFlightRef.current = true;
    void Promise.resolve(loadRef.current()).finally(() => {
      inFlightRef.current = false;
    });
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const onVisibility = () => {
      if (document.visibilityState === 'visible') run();
    };
    const onFocus = () => run();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [enabled, run]);
}
